import React, { useId, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './AppointmentOptionPicker.module.css';
import type { ServiceOptionGroupDto, ServiceOptionItemDto } from '../../types/serviceOptionGroups';
import {
    computeSelectionTotals,
    findAddOnGroups,
    findVariantGroup,
    type AppointmentOptionSelection,
} from '../../lib/serviceOptionGroups/appointmentOptionSelection';
import { durationStringToMinutes } from '../../lib/serviceOptionGroups/serviceOptionGroups';

/**
 * PBI 1962 (AB#1962) — Owner manual-booking option picker.
 *
 * Renders the selected service's option groups for a manual appointment:
 *   - the Variant group as a REQUIRED radio list (absolute price/duration);
 *   - each Add-on group as checkboxes ("+X AED · +Y min" additive deltas)
 *     with min/max advisory enforcement (unchecked boxes disable at max);
 *   - a live total (price + duration). The server recomputes authoritatively.
 *
 * Mirrors the Phase-3 ServiceOptionGroupsBuilder visual language (group cards
 * with a kind accent, micro labels, LTR-isolated numerics) in read/select
 * form. Fully controlled via `selection` + `onChange`.
 */
export interface AppointmentOptionPickerProps {
    groups: ServiceOptionGroupDto[];
    selection: AppointmentOptionSelection;
    onChange: (next: AppointmentOptionSelection) => void;
    disabled?: boolean;
    /** Show the variant-required error (after a failed submit attempt). */
    showErrors?: boolean;
}

const formatPrice = (price: number): string => price.toFixed(2);

const AppointmentOptionPicker: React.FC<AppointmentOptionPickerProps> = ({
    groups,
    selection,
    onChange,
    disabled = false,
    showErrors = false,
}) => {
    const { t, isRTL } = useLanguage();
    const baseId = useId();

    const variantGroup = useMemo(() => findVariantGroup(groups), [groups]);
    const addOnGroups = useMemo(() => findAddOnGroups(groups), [groups]);
    const totals = useMemo(
        () => computeSelectionTotals(groups, selection),
        [groups, selection],
    );

    if (!variantGroup) return null;

    const variantMissing = !variantGroup.options.some(
        (o) => o.id === selection.variantOptionId,
    );

    const localizedName = (name: string, nameAr?: string | null): string =>
        isRTL && nameAr ? nameAr : name;

    const pickVariant = (optionId: number) =>
        onChange({ ...selection, variantOptionId: optionId });

    const toggleAddOn = (optionId: number, checked: boolean) =>
        onChange({
            ...selection,
            addOnOptionIds: checked
                ? [...selection.addOnOptionIds, optionId]
                : selection.addOnOptionIds.filter((id) => id !== optionId),
        });

    const renderVariantRow = (option: ServiceOptionItemDto) => {
        const checked = selection.variantOptionId === option.id;
        const inputId = `${baseId}-v-${option.id}`;
        return (
            <label
                key={option.id}
                htmlFor={inputId}
                className={`${styles.optionRow} ${checked ? styles.optionRowSelected : ''}`}
            >
                <input
                    id={inputId}
                    type="radio"
                    className={styles.control}
                    name={`${baseId}-variant`}
                    checked={checked}
                    onChange={() => pickVariant(option.id)}
                    disabled={disabled}
                />
                <span className={styles.optionName}>
                    {localizedName(option.name, option.nameAr)}
                </span>
                <span className={styles.optionMeta} dir="ltr">
                    {formatPrice(option.price)} {option.currency}
                    {' · '}
                    {durationStringToMinutes(option.duration)} {t('services.builder.durationMinutes')}
                </span>
            </label>
        );
    };

    const renderAddOnGroup = (group: ServiceOptionGroupDto) => {
        const selectedCount = group.options.filter((o) =>
            selection.addOnOptionIds.includes(o.id),
        ).length;
        const atMax = selectedCount >= group.maxSelect;
        const hint =
            group.minSelect >= 1
                ? t('appointments.options.chooseAtLeast').replace('{n}', String(group.minSelect))
                : t('services.builder.optional');
        const maxHint = t('appointments.options.upTo').replace('{n}', String(group.maxSelect));

        return (
            <fieldset key={group.id} className={`${styles.group} ${styles.groupAddon}`}>
                <legend className={styles.groupLegend}>
                    <span className={styles.groupName}>
                        {localizedName(group.name, group.nameAr)}
                    </span>
                    <span className={styles.kindBadge}>{t('services.builder.addon')}</span>
                    <span className={styles.groupHint}>
                        {hint}
                        {' · '}
                        {maxHint}
                    </span>
                </legend>
                <div className={styles.optionList}>
                    {group.options.map((option) => {
                        const checked = selection.addOnOptionIds.includes(option.id);
                        const inputId = `${baseId}-a-${option.id}`;
                        return (
                            <label
                                key={option.id}
                                htmlFor={inputId}
                                className={`${styles.optionRow} ${checked ? styles.optionRowSelected : ''} ${!checked && atMax ? styles.optionRowDisabled : ''}`}
                            >
                                <input
                                    id={inputId}
                                    type="checkbox"
                                    className={styles.control}
                                    checked={checked}
                                    onChange={(e) => toggleAddOn(option.id, e.target.checked)}
                                    disabled={disabled || (!checked && atMax)}
                                />
                                <span className={styles.optionName}>
                                    {localizedName(option.name, option.nameAr)}
                                </span>
                                <span className={styles.optionMeta} dir="ltr">
                                    +{formatPrice(option.price)} {option.currency}
                                    {' · '}
                                    +{durationStringToMinutes(option.duration)} {t('services.builder.durationMinutes')}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </fieldset>
        );
    };

    return (
        <div className={styles.picker}>
            <fieldset className={`${styles.group} ${styles.groupVariant}`}>
                <legend className={styles.groupLegend}>
                    <span className={styles.groupName}>
                        {localizedName(variantGroup.name, variantGroup.nameAr)}
                    </span>
                    <span className={styles.kindBadge}>{t('services.builder.variant')}</span>
                    <span className={styles.groupHint}>{t('services.builder.required')}</span>
                </legend>
                <div className={styles.optionList} role="radiogroup">
                    {variantGroup.options.map(renderVariantRow)}
                </div>
                {showErrors && variantMissing && (
                    <p className={styles.error} role="alert">
                        {t('error.option.variantRequired')}
                    </p>
                )}
            </fieldset>

            {addOnGroups.map(renderAddOnGroup)}

            <div className={styles.totalBar} aria-live="polite">
                <span className={styles.totalLabel}>{t('appointments.options.total')}</span>
                {totals ? (
                    <span className={styles.totalValue} dir="ltr">
                        {formatPrice(totals.price)} {totals.currency}
                        {' · '}
                        {totals.durationMin} {t('services.builder.durationMinutes')}
                    </span>
                ) : (
                    <span className={styles.totalValue}>—</span>
                )}
            </div>
            <p className={styles.serverNote}>{t('appointments.options.serverTotalNote')}</p>
        </div>
    );
};

export default AppointmentOptionPicker;
